import {
  RestaurantPosDefaultTabV2,
  DEFAULT_PAID_SALE_AUTO_CLOSE_SECONDS,
} from '@am3nd-org/pointcraft-types';
import type {
  PermissionsEnum,
  DeviceSyncTerminal,
  BusinessDataV2Type,
  BusinessUserV2Client,
  SingleUserProfileType,
} from '@am3nd-org/pointcraft-types';

import { useDeviceContextStore } from 'src/store/DeviceContext';
import type { IDeviceItem, ITerminalItem, CurrentTerminal } from 'src/store/SystemStore/types';

import { useDeviceSyncStore, type DeviceSyncState } from './index';

const terminalListItemCache = new WeakMap<DeviceSyncTerminal, ITerminalItem>();
const EMPTY_ITEM_CATEGORIES: string[] = [];

type BusinessData = BusinessDataV2Type | null | undefined;

function settingsFor(business: BusinessData) {
  return business?.access_v2?.businessAccess?.settings;
}

/** Business-wide order processing is disabled unless Access V2 explicitly enables it. */
export function resolveOrdersEnabled(business: BusinessData): boolean {
  return settingsFor(business)?.orders?.enabled ?? false;
}

export function resolveAutoAcceptOrders(business: BusinessData): boolean {
  return settingsFor(business)?.orders?.autoAcceptOrders ?? false;
}

export function resolveAutoCompleteOrders(business: BusinessData): boolean {
  return settingsFor(business)?.orders?.autoCompleteOrders ?? false;
}

export function resolveAutoPrintReceipt(business: BusinessData): boolean {
  return settingsFor(business)?.printing?.autoPrintReceipt ?? false;
}

export function resolveLockoutTimeMinutes(business: BusinessData): number {
  return settingsFor(business)?.system?.lockoutTimeMinutes ?? 0;
}

export function resolveReceiptFooterText(business: BusinessData): string {
  return settingsFor(business)?.printing?.footerText ?? '';
}

export function resolveDisplayBiggerText(business: BusinessData): boolean {
  return settingsFor(business)?.ui?.displayBiggerText ?? false;
}

export function resolveHoldSalesEnabled(business: BusinessData): boolean {
  return settingsFor(business)?.checkout?.holdSalesEnabled ?? false;
}

/**
 * A missing paid-sale setting keeps the established V5 behavior enabled.
 * Missing business data is different: wait for device sync before starting a
 * durable deadline so a configured opt-out cannot briefly race the preload.
 */
export function resolvePaidSaleAutoCloseEnabled(business: BusinessData): boolean | null {
  if (!business) return null;
  return settingsFor(business)?.payments?.paidSaleAutoCloseEnabled ?? true;
}

export function resolvePaidSaleAutoCloseSeconds(business: BusinessData): number {
  return (
    settingsFor(business)?.payments?.paidSaleAutoCloseSeconds ??
    DEFAULT_PAID_SALE_AUTO_CLOSE_SECONDS
  );
}

export function resolveRestaurantPosDefaultViewTab(
  business: BusinessData
): RestaurantPosDefaultTabV2 {
  return (
    settingsFor(business)?.checkout?.restaurantPosDefaultViewTab ??
    RestaurantPosDefaultTabV2.CUSTOM
  );
}

export const selectOrdersEnabled = (state: DeviceSyncState): boolean =>
  resolveOrdersEnabled(state.businessData);
export const selectAutoAcceptOrders = (state: DeviceSyncState): boolean =>
  resolveAutoAcceptOrders(state.businessData);
export const selectAutoCompleteOrders = (state: DeviceSyncState): boolean =>
  resolveAutoCompleteOrders(state.businessData);
export const selectAutoPrintReceipt = (state: DeviceSyncState): boolean =>
  resolveAutoPrintReceipt(state.businessData);
export const selectLockoutTimeMinutes = (state: DeviceSyncState): number =>
  resolveLockoutTimeMinutes(state.businessData);
export const selectReceiptFooterText = (state: DeviceSyncState): string =>
  resolveReceiptFooterText(state.businessData);
export const selectDisplayBiggerText = (state: DeviceSyncState): boolean =>
  resolveDisplayBiggerText(state.businessData);
export const selectHoldSalesEnabled = (state: DeviceSyncState): boolean =>
  resolveHoldSalesEnabled(state.businessData);
export const selectPaidSaleAutoCloseEnabled = (state: DeviceSyncState): boolean | null =>
  resolvePaidSaleAutoCloseEnabled(state.businessData);
export const selectPaidSaleAutoCloseSeconds = (state: DeviceSyncState): number =>
  resolvePaidSaleAutoCloseSeconds(state.businessData);
export const selectRestaurantPosDefaultViewTab = (
  state: DeviceSyncState
): RestaurantPosDefaultTabV2 => resolveRestaurantPosDefaultViewTab(state.businessData);

/** Account Access V2 is the canonical category source for migrated catalog screens. */
export function selectAccessV2ItemCategories(state: DeviceSyncState): string[] {
  return (
    state.businessData?.access_v2.businessAccess.settings.catalog?.itemCategories ??
    EMPTY_ITEM_CATEGORIES
  );
}

export function toTerminalListItem(terminal: DeviceSyncTerminal): ITerminalItem {
  const cached = terminalListItemCache.get(terminal);
  if (cached) return cached;

  const terminalWithProviderLocation = terminal as DeviceSyncTerminal & {
    providerLocationId?: string | null;
  };
  const item: ITerminalItem = {
    id: terminal.id,
    name: terminal.name,
    deviceTag: terminal.deviceTag,
    executionMode: terminal.executionMode,
    status: terminal.status,
    type: terminal.type,
    serialNumber: terminal.serialNumber ?? '',
    ...(terminal.businessLocationId ? { locationId: terminal.businessLocationId } : {}),
    provider: terminal.provider as ITerminalItem['provider'],
    providerTerminalId: terminal.providerTerminalId,
    ...(terminalWithProviderLocation.providerLocationId
      ? { providerLocationId: terminalWithProviderLocation.providerLocationId }
      : {}),
    ...(terminal.localIpAddress !== undefined
      ? { localIpAddress: terminal.localIpAddress }
      : {}),
  };
  terminalListItemCache.set(terminal, item);
  return item;
}

let cachedTerminalSource: DeviceSyncState['terminals'] | null = null;
let cachedTerminalItems: ITerminalItem[] = [];

export function selectTerminalListItems(state: DeviceSyncState): ITerminalItem[] {
  if (state.terminals === cachedTerminalSource) return cachedTerminalItems;
  cachedTerminalSource = state.terminals;
  cachedTerminalItems = state.terminals.map(toTerminalListItem);
  return cachedTerminalItems;
}

let cachedDeviceSource: DeviceSyncState['businessDevices'] | null = null;
let cachedDeviceTerminalSource: DeviceSyncState['terminals'] | null = null;
let cachedDeviceItems: IDeviceItem[] = [];

export function selectBusinessDeviceListItems(state: DeviceSyncState): IDeviceItem[] {
  if (
    state.businessDevices === cachedDeviceSource &&
    state.terminals === cachedDeviceTerminalSource
  ) {
    return cachedDeviceItems;
  }

  cachedDeviceSource = state.businessDevices;
  cachedDeviceTerminalSource = state.terminals;
  const terminalById = new Map(
    selectTerminalListItems(state).map((terminal) => [terminal.id, terminal])
  );

  cachedDeviceItems = state.businessDevices.map((device) => ({
    id: device.id,
    name: device.deviceName ?? 'Unnamed device',
    serialNumber: device.serialNumber ?? '',
    lastConnected: device.lastPairedAt ?? undefined,
    isActive: device.status === 'ACTIVE',
    ...(device.terminalId && terminalById.has(device.terminalId)
      ? { terminal: terminalById.get(device.terminalId) }
      : {}),
  }));
  return cachedDeviceItems;
}

export type TerminalLocationOption = { id: string; name: string };

let cachedLocationTerminals: DeviceSyncState['terminals'] | null = null;
let cachedTerminalLocations: TerminalLocationOption[] = [];

/** Location ids exposed by the V4 terminal slice, for device configuration forms. */
export function selectTerminalLocations(state: DeviceSyncState): TerminalLocationOption[] {
  if (state.terminals === cachedLocationTerminals) return cachedTerminalLocations;

  cachedLocationTerminals = state.terminals;
  const locations = new Map<string, TerminalLocationOption>();
  state.terminals.forEach((terminal) => {
    if (terminal.businessLocationId && !locations.has(terminal.businessLocationId)) {
      locations.set(terminal.businessLocationId, {
        id: terminal.businessLocationId,
        name: terminal.name || terminal.businessLocationId,
      });
    }
  });
  cachedTerminalLocations = Array.from(locations.values());
  return cachedTerminalLocations;
}

export function selectCurrentUser(state: DeviceSyncState): BusinessUserV2Client | null {
  return (
    state.userData.find((user) => user.isDefault) ??
    state.userData.find((user) => user.isActive) ??
    null
  );
}

let cachedSaleEmployeeSource: BusinessUserV2Client | null = null;
let cachedSaleEmployeeBusiness: DeviceSyncState['businessData'] = null;
let cachedSaleEmployee: SingleUserProfileType | null = null;

/** Maps the current V4 business user into the employee snapshot stored on a sale. */
export function selectCurrentSaleEmployee(state: DeviceSyncState): SingleUserProfileType | null {
  const user = selectCurrentUser(state);
  const business = state.businessData;
  if (!user || !business) return null;

  if (
    user === cachedSaleEmployeeSource &&
    business === cachedSaleEmployeeBusiness &&
    cachedSaleEmployee
  ) {
    return cachedSaleEmployee;
  }

  cachedSaleEmployeeSource = user;
  cachedSaleEmployeeBusiness = business;
  cachedSaleEmployee = {
    name: user.profile.name,
    userId: user.id,
    email: user.profile.email,
    ...(user.profile.address ? { address: user.profile.address } : {}),
    ...(user.profile.phone ? { phone: user.profile.phone } : {}),
    isActive: user.isActive,
    isSuperAdmin: business.isSuperAdmin,
    isDefault: user.isDefault,
    features: business.configurations.features,
    permissions: business.configurations.permissions,
    configurations: {
      virtualKeyboard: false,
      notificationPreferences: user.notificationPreferences,
    },
    role: user.roleId,
    roleId: user.roleId,
  };
  return cachedSaleEmployee;
}

export function selectHasPermission(state: DeviceSyncState, permission: PermissionsEnum): boolean {
  return Boolean(state.businessData?.configurations.permissions.includes(permission));
}

export function getCurrentDeviceTerminal(): CurrentTerminal | null {
  const { terminalId } = useDeviceContextStore.getState();
  if (!terminalId) return null;
  const terminal = useDeviceSyncStore.getState().terminals.find((item) => item.id === terminalId);
  return terminal ? toTerminalListItem(terminal) : null;
}

export function useCurrentDeviceTerminal(): CurrentTerminal | null {
  const terminalId = useDeviceContextStore((state) => state.terminalId);
  return useDeviceSyncStore((state) => {
    const terminal = state.terminals.find((item) => item.id === terminalId);
    return terminal ? toTerminalListItem(terminal) : null;
  });
}
